import { supabase } from '../../config/supabase';

export interface ChangelogFeature {
  id: string;
  title: string;
  description: string;
  type: 'feature' | 'improvement' | 'bugfix';
  order_index: number;
}

export interface DynamicChangelog {
  id: string;
  version: string;
  date: string;
  is_active: boolean;
  features: ChangelogFeature[];
}

export class DynamicChangelogService {
  static async getActiveChangelog(): Promise<DynamicChangelog | null> {
    const { data, error } = await supabase
      .from('changelog')
      .select(`
        id,
        version,
        date,
        is_active,
        changelog_features (
          id,
          title,
          description,
          type,
          order_index
        )
      `)
      .eq('is_active', true)
      .order('date', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Pas de changelog actif
      }
      throw error;
    }

    return {
      id: data.id,
      version: data.version,
      date: data.date,
      is_active: data.is_active,
      features: (data.changelog_features || [])
        .sort((a: ChangelogFeature, b: ChangelogFeature) => a.order_index - b.order_index)
    };
  }

  static async getAllChangelogs(): Promise<DynamicChangelog[]> {
    const { data, error } = await supabase
      .from('changelog')
      .select(`
        id,
        version,
        date,
        is_active,
        changelog_features (
          id,
          title,
          description,
          type,
          order_index
        )
      `)
      .order('date', { ascending: false });

    if (error) throw error;

    return (data || []).map(changelog => ({
      id: changelog.id,
      version: changelog.version,
      date: changelog.date,
      is_active: changelog.is_active,
      features: (changelog.changelog_features || [])
        .sort((a: ChangelogFeature, b: ChangelogFeature) => a.order_index - b.order_index)
    }));
  }

  static async createChangelog(changelog: Omit<DynamicChangelog, 'id' | 'features'>): Promise<string> {
    const { data, error } = await supabase
      .from('changelog')
      .insert({
        version: changelog.version,
        date: changelog.date,
        is_active: changelog.is_active
      })
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  }

  static async addFeature(changelogId: string, feature: Omit<ChangelogFeature, 'id'>): Promise<void> {
    const { error } = await supabase
      .from('changelog_features')
      .insert({
        changelog_id: changelogId,
        title: feature.title,
        description: feature.description,
        type: feature.type,
        order_index: feature.order_index
      });

    if (error) throw error;
  }

  static async activateChangelog(version: string): Promise<void> {
    // Désactiver tous les autres changelogs
    await supabase
      .from('changelog')
      .update({ is_active: false })
      .neq('version', version);

    // Activer le changelog demandé
    const { error } = await supabase
      .from('changelog')
      .update({ is_active: true })
      .eq('version', version);

    if (error) throw error;
  }
}
